import React, { useMemo } from 'react';
import {
    Box,
    Chip,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { formatCurrency } from '../../qCockpit/components/SharedFrames';
import { shellTokens } from '../../shell/theme/shellTheme';
import { clientColumnKey } from '../clientAlias';

const EMPTY = '—';

const filterByMode = (instances, modeFilter) => {
    if (modeFilter === 'all') {
        return instances;
    }
    return instances.filter((instance) => instance.mode === modeFilter);
};

const buildMatrixAxes = (instances, viewMode) => {
    if (viewMode === 'client') {
        const rows = [...new Map(instances.map((item) => [clientColumnKey(item), clientColumnKey(item)])).entries()]
            .sort((a, b) => a[1].localeCompare(b[1]));
        const cols = [...new Map(instances.map((item) => [item.strategyId, item.strategyLabel || item.strategyId])).entries()]
            .sort((a, b) => a[1].localeCompare(b[1]));
        return { rows, cols };
    }

    const rows = [...new Map(instances.map((item) => [item.strategyId, item.strategyLabel || item.strategyId])).entries()]
        .sort((a, b) => a[1].localeCompare(b[1]));
    const cols = [...new Map(instances.map((item) => [clientColumnKey(item), clientColumnKey(item)])).entries()]
        .sort((a, b) => a[1].localeCompare(b[1]));
    return { rows, cols };
};

const findInstance = (instances, rowId, colId, viewMode) => {
    if (viewMode === 'client') {
        return instances.find((item) => clientColumnKey(item) === rowId && item.strategyId === colId);
    }
    return instances.find((item) => item.strategyId === rowId && clientColumnKey(item) === colId);
};

const rowNetPnl = (instances, rowId, viewMode) =>
    instances
        .filter((item) => (viewMode === 'client' ? clientColumnKey(item) === rowId : item.strategyId === rowId))
        .reduce((sum, item) => sum + (item.netPnl || 0), 0);

const pnlColor = (value) => {
    if (value > 0) return shellTokens.success;
    if (value < 0) return shellTokens.error;
    return shellTokens.text;
};

const ModeBadge = ({ mode }) => (
    <Chip
        label={mode}
        size="small"
        sx={{
            height: 20,
            fontSize: '0.64rem',
            fontWeight: 700,
            mr: 0.75,
            bgcolor: mode === 'live' ? 'rgba(22, 163, 74, 0.1)' : 'rgba(100, 116, 139, 0.12)',
            color: mode === 'live' ? shellTokens.success : shellTokens.muted,
        }}
    />
);

const MatrixCell = ({ instance, selected, onSelect }) => {
    if (!instance) {
        return (
            <Typography sx={{ color: '#cbd5e1', fontSize: '0.82rem' }}>
                {EMPTY}
            </Typography>
        );
    }

    return (
        <Box
            component="button"
            type="button"
            onClick={() => onSelect(instance.instanceId)}
            sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 0.5,
                width: '100%',
                border: '1px solid',
                borderColor: selected ? shellTokens.primary : 'transparent',
                borderRadius: `${shellTokens.innerRadius}px`,
                bgcolor: selected ? 'rgba(37, 99, 235, 0.06)' : 'transparent',
                p: 0.75,
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: 'inherit',
            }}
        >
            <ModeBadge mode={instance.mode} />
            {instance.openPositionCount > 0 ? (
                <Typography component="span" sx={{ fontSize: '0.7rem', color: shellTokens.primary, mr: 0.25 }}>
                    ●
                </Typography>
            ) : null}
            <Typography
                component="span"
                sx={{
                    fontFamily: shellTokens.monoFamily,
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    color: pnlColor(instance.netPnl),
                }}
            >
                {formatCurrency(instance.netPnl)}
            </Typography>
            {instance.isStale ? (
                <Chip label="stale" size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: 'rgba(217, 119, 6, 0.12)', color: shellTokens.warning }} />
            ) : null}
            {instance.isHalted ? (
                <Chip label="halt" size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: 'rgba(220, 38, 38, 0.1)', color: shellTokens.error }} />
            ) : null}
        </Box>
    );
};

const FleetMatrix = ({
    instances = [],
    viewMode,
    modeFilter,
    selectedInstanceId,
    onSelectInstance,
    loading,
}) => {
    const filteredInstances = useMemo(
        () => filterByMode(instances, modeFilter),
        [instances, modeFilter],
    );
    const { rows, cols } = useMemo(
        () => buildMatrixAxes(filteredInstances, viewMode),
        [filteredInstances, viewMode],
    );

    const rowHeader = viewMode === 'client' ? 'Client' : 'Strategy';

    return (
        <Box sx={{ bgcolor: shellTokens.surface, border: `1px solid ${shellTokens.border}`, borderRadius: `${shellTokens.radius}px`, overflow: 'hidden', mb: 1.75 }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${shellTokens.border}` }}>
                <Typography
                    sx={{
                        fontSize: '0.62rem',
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: shellTokens.muted,
                    }}
                >
                    StrategyDesk
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', mt: 0.25 }}>
                    Fleet matrix (compact)
                </Typography>
            </Box>

            <Box sx={{ p: 2 }}>
                {loading ? (
                    <Skeleton variant="rounded" height={220} />
                ) : filteredInstances.length === 0 ? (
                    <Typography sx={{ color: shellTokens.muted, fontSize: '0.84rem' }}>
                        No fleet instances match the current filters.
                    </Typography>
                ) : (
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small" sx={{ minWidth: 560 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.76rem' }}>{rowHeader}</TableCell>
                                    {cols.map(([colId, colLabel]) => (
                                        <TableCell key={colId} sx={{ fontWeight: 700, fontSize: '0.76rem' }}>
                                            {colLabel}
                                        </TableCell>
                                    ))}
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.76rem' }}>Net</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map(([rowId, rowLabel]) => {
                                    const net = rowNetPnl(filteredInstances, rowId, viewMode);
                                    return (
                                        <TableRow key={rowId} hover>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                                                {rowLabel}
                                            </TableCell>
                                            {cols.map(([colId]) => {
                                                const instance = findInstance(filteredInstances, rowId, colId, viewMode);
                                                return (
                                                    <TableCell key={colId} sx={{ verticalAlign: 'top', minWidth: 140 }}>
                                                        <MatrixCell
                                                            instance={instance}
                                                            selected={Boolean(instance && instance.instanceId === selectedInstanceId)}
                                                            onSelect={onSelectInstance}
                                                        />
                                                    </TableCell>
                                                );
                                            })}
                                            <TableCell sx={{ fontFamily: shellTokens.monoFamily, fontWeight: 700, color: pnlColor(net), whiteSpace: 'nowrap' }}>
                                                {formatCurrency(net)}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
        </Box>
    );
};

export default FleetMatrix;
